import type { ComponentProps } from 'react';
import { useViewing, viewSentence } from '../view-as.js';

/**
 * A write control keeps its place, and its look, while the keeper reads the page. The 13 Oct 2026 decision, "Same look,
 * locked (Recommended)": under a view the button is drawn as the person sees it (the yellow lead, the filled options), so the
 * page matches theirs; it is marked aria-disabled, says the band's sentence on hover, and a press (or a form's implicit
 * submit, which arrives as a click) does nothing. A real `disabled` greyed every option and the lead, and the view stopped
 * looking like the person's own screen. A control disabled for its own reason (busy, nothing picked) stays disabled.
 */
export function ActionButton({ reading = false, ...props }: ComponentProps<'button'> & { reading?: boolean }) {
  const view = useViewing();
  const locked = !!view && !reading;
  if (!locked) return <button {...props} />;
  return <button {...props} aria-disabled="true" data-view-locked="" title={viewSentence(view)}
    onClick={(event) => { event.preventDefault(); event.stopPropagation(); }} />;
}

/**
 * `reading` marks a control that only narrows what is shown (a filter, a period, a search field, a date): it stays usable
 * under a view, as an ActionButton marked `reading` does, because changing it writes nothing. Track D's ask, 14 Oct 2026.
 */
export function ActionSelect({ reading = false, ...props }: ComponentProps<'select'> & { reading?: boolean }) {
  const view = useViewing();
  const locked = !!view && !reading;
  return <select {...props} disabled={locked || props.disabled} title={locked ? viewSentence(view) : props.title} />;
}

export function ActionInput({ reading = false, ...props }: ComponentProps<'input'> & { reading?: boolean }) {
  const view = useViewing();
  const locked = !!view && !reading;
  return <input {...props} disabled={locked || props.disabled} title={locked ? viewSentence(view) : props.title} />;
}

export function ActionTextarea({ reading = false, ...props }: ComponentProps<'textarea'> & { reading?: boolean }) {
  const view = useViewing();
  const locked = !!view && !reading;
  return <textarea {...props} disabled={locked || props.disabled} title={locked ? viewSentence(view) : props.title} />;
}

export function ActionLink(props: ComponentProps<'a'>) {
  const view = useViewing();
  return <a {...props} aria-disabled={view ? true : props['aria-disabled']} title={view ? viewSentence(view) : props.title}
    onClick={(event) => { if (view) event.preventDefault(); else props.onClick?.(event); }} />;
}
